import clsx from "clsx";
import { useEffect } from "react";
import { flash } from "../../web/lib/flash";
import { fraction, rateFor, type TaxRate, toNumber } from "../../shared/money";
import { ChoiceField, TextField } from "../../web/components/fields";
import { InlinePicker } from "../../web/components/InlinePicker";
import { Notice, PageHeader, Panel } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { can, usePriceListSaver, whyNot } from "./data";
import { MONEY_PATTERN, type PriceListData, shortNameTaken } from "./meta";
import { JobTimeSettings } from "./JobTimeSettings";
import { NameTagSettings } from "./NameTagSettings";
import { hasLabourCost, labourRateNet } from "./pricing";
import { taxChoice } from "./QuotedAmount";

/** An amount shown as entered, or blank ("Not set") when there's none: $0 an hour isn't a real rate or cost. */
const setOrBlank = (amount: string) => (toNumber(amount) > 0 ? amount : "");

/** An amount, or blank for not set. */
const amountOrBlank = (example: string) => (v: string) => (v === "" || (MONEY_PATTERN.test(v) && Number(v) >= 0) ? null : `Enter an amount, like ${example}, or leave it blank`);

/*
 * Tax, whatever the business's rules (its ServiceM8 tax rates): labour is taxed at each item's own
 * rate, so nothing here names a rate that may not apply. With one rate that charges tax (e.g. GST,
 * beside GST-free), it's called by name; with several, "tax"; with none, there's no choice to make.
 */

/** The business's rates that charge tax (above 0%). */
const taxingRates = (rates: readonly TaxRate[]) => rates.filter((r) => r.percent > 0);

/** What its tax is called: its one taxing rate's name (e.g. "GST"), else "tax". */
const taxWordOf = (rates: readonly TaxRate[]) => {
    const taxing = taxingRates(rates);
    return taxing.length === 1 ? (taxing[0]?.name ?? "tax") : "tax";
};

/**
 * What an hour of labour makes before tax (what's charged, less what it costs), once both are set.
 * The charge's tax is taken out at the business's one taxing rate; with several, at its default
 * rate, which it says (items taxed otherwise differ).
 */
function HourMakes({ data }: Readonly<{ data: PriceListData }>) {
    const fmt = useMoney();
    const s = data.settings;
    if (toNumber(s.labourRate) <= 0 || !hasLabourCost(s)) return null;
    const taxing = taxingRates(data.taxRates);
    const rate = taxing.length === 1 ? taxing[0] : rateFor("", data.taxRates);
    const charged = labourRateNet(s, fraction(rate));
    // What it costs: wages, with no tax.
    const cost = toNumber(s.labourCost);
    const makes = charged - cost;
    // Before tax (its tax taken out; with several rates, at which one).
    let basis = "";
    if (taxing.length === 1) basis = " before tax";
    else if (taxing.length > 1) basis = rate && rate.percent > 0 ? ` before tax (at ${rate.name}, ${rate.percent}%)` : " before tax";
    return (
        <p className={clsx("m-0 mt-4 border-t border-line pt-3 text-sm", makes < 0 ? "text-danger" : "text-muted")}>
            Each hour of labour {makes < 0 ? "loses" : "makes"} <strong className="tabular-nums">{fmt.money(Math.abs(makes))}</strong>
            {basis}: {fmt.money(charged)} charged, {fmt.money(cost)} cost.
        </p>
    );
}

/**
 * A short name to suggest for a long ServiceM8 tax rate name: what's before " on " ("GST on
 * Income": "GST"), else the initials of a name of three words or more ("Goods and Services Tax":
 * "GST", small words like "and" left out), else none.
 */
function suggestShortName(name: string) {
    const at = name.toLowerCase().indexOf(" on ");
    if (at > 0) return name.slice(0, at).trim();
    const words = name.trim().split(" ").filter(Boolean);
    // Already has a short form in it ("GST Free Income"): initials would only confuse ("GFI").
    if (words.length < 3 || words.some((w) => w.length > 1 && w === w.toUpperCase())) return "";
    return words
        .filter((w) => w[0] === w[0]?.toUpperCase())
        .map((w) => w[0])
        .join("");
}

/**
 * Short names for the business's ServiceM8 tax rates, shown in their place across the Price List
 * ("GST" for "GST on Income"); blank, its full name. Only kept here: ServiceM8's names (and the
 * accounting software's link to them) never change. Nothing without tax rates.
 */
function TaxRateNames({ data, disabled }: Readonly<{ data: PriceListData; disabled: boolean }>) {
    const saver = usePriceListSaver();
    const names = data.settings.taxShortNames;
    if (!data.taxRates.length) return null;
    // An example from its own rates ("GST on Income" as "GST"), if one has a long name to shorten.
    const example = data.taxRates.map((r) => ({ full: r.fullName ?? r.name, short: suggestShortName(r.fullName ?? r.name) })).find((e) => e.short);
    // Only this rate's (the server keeps the rest), so quick edits to two rates can't undo each other; blank: its full name again.
    const save = (uuid: string, value: string) => saver.saveSettings({ taxShortNames: { [uuid]: value } });
    return (
        <Panel title="Tax rates" id="tax-rates">
            <p className="m-0 mb-3 text-sm text-muted">
                Give a long tax rate name a short one to show across the Price List{example ? <>, e.g. &quot;{example.full}&quot; as &quot;{example.short}&quot;</> : null}. Leave it blank to use the
                full name. Only used here: never sent to ServiceM8.
            </p>
            {/* A small table: each rate's name and percentage as ServiceM8 has them, and its short name. */}
            <div className="max-w-xl">
                <div className="grid grid-cols-[minmax(0,1fr)_4rem_minmax(0,1fr)] gap-3 border-b border-line pb-1.5 text-xs text-muted">
                    <span>Name in ServiceM8</span>
                    <span>Rate</span>
                    <span>Short name</span>
                </div>
                {data.taxRates.map((r) => {
                    const full = r.fullName ?? r.name;
                    return (
                        <div key={r.uuid} className="grid grid-cols-[minmax(0,1fr)_4rem_minmax(0,1fr)] items-center gap-3 border-b border-line py-1.5 last:border-b-0">
                            <span className="truncate text-sm" title={full}>
                                {full}
                            </span>
                            <span className="text-sm text-muted tabular-nums">{r.percent}%</span>
                            <TextField
                                hideLabel
                                label={`Short name for ${full}`}
                                value={names[r.uuid] ?? ""}
                                placeholder={suggestShortName(full) ? `e.g. ${suggestShortName(full)}` : "Not set"}
                                disabled={disabled}
                                validate={(v) => {
                                    if (v.trim().length > 20) return "At most 20 characters";
                                    const taken = shortNameTaken(data.taxRates, names, r.uuid, v);
                                    return taken ? `${taken} reads as that already` : null;
                                }}
                                onSave={(v) => save(r.uuid, v.trim())}
                            />
                        </div>
                    );
                })}
            </div>
        </Panel>
    );
}

/** Price List settings: the labour rate, how tax on purchases is treated, when supplier prices are due a recheck, the minimum markup, name tags and job time. */
export function SettingsSection({ data }: Readonly<{ data: PriceListData }>) {
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const s = data.settings;
    const disabled = !can(data, "settings");
    // Its tax by name, or "tax" (taxWordOf); none charged at all: no inc/ex anywhere.
    const taxWord = taxWordOf(data.taxRates);
    const taxed = taxingRates(data.taxRates).length > 0;
    // Links to a panel (an item's "Price List settings" links go to #name-tags and #labour) open scrolled to it.
    // ...and flashed, so the eye lands on it (as a warning's Show does): each time a link brings you here, this page already
    // showing too (a link's # said: RecordWindow.tsx).
    useEffect(() => {
        const show = () => {
            const hash = globalThis.location.hash.slice(1);
            const panel = hash ? document.getElementById(hash) : null;
            if (!panel) return;
            panel.scrollIntoView({ block: "start", behavior: "smooth" });
            flash(panel, "is-flashed");
        };
        show();
        globalThis.addEventListener("hashchange", show);
        return () => globalThis.removeEventListener("hashchange", show);
    }, []);

    return (
        <div className="page is-wide">
            <PageHeader title="Price List settings" subtitle="Used by every item's pricing and name." />
            {whyNot(data, "settings") ? (
                <div className="mb-4">
                    <Notice>{whyNot(data, "settings")}</Notice>
                </div>
            ) : null}
            {/* Every panel on the same two columns: a field is one, so nothing stretches across the page. */}
            {/* What an hour of labour is charged at, and (See costs) what it costs: side by side, each "Not set" until it's entered. */}
            <Panel title="Labour" id="labour">
                {/* The same two boxes: how the charge is taxed is picked inside it, as every taxed amount is (QuotedAmount.tsx); wages carry no tax. */}
                <div className="grid items-start gap-4 sm:grid-cols-2">
                    <TextField
                        label="Charge per hour"
                        value={setOrBlank(s.labourRate)}
                        prefix={fmt.symbol}
                        suffix={
                            taxed ? (
                                <InlinePicker
                                    label="How the charge is taxed"
                                    // The same choices, notes and order as every tax picker (QuotedAmount.tsx taxChoice).
                                    options={[
                                        { value: "ex", ...taxChoice.ex(taxWord) },
                                        { value: "inc", ...taxChoice.inc(taxWord) },
                                    ]}
                                    value={s.labourRateIncludesTax ? "inc" : "ex"}
                                    disabled={disabled}
                                    onChange={(v) => void saver.saveSettings({ labourRateIncludesTax: v === "inc" })}
                                />
                            ) : undefined
                        }
                        placeholder="Not set"
                        inputMode="decimal"
                        disabled={disabled}
                        validate={amountOrBlank("120")}
                        hint="What you charge. Items with labour add it for their time."
                        onSave={(labourRate) => saver.saveSettings({ labourRate })}
                    />
                    {/* A cost: only for those who may see costs (the server leaves it out otherwise). */}
                    {can(data, "costs.view") ? (
                        <TextField
                            label="Cost per hour"
                            value={setOrBlank(s.labourCost)}
                            prefix={fmt.symbol}
                            placeholder="Not set"
                            inputMode="decimal"
                            disabled={disabled}
                            validate={amountOrBlank("45")}
                            hint="What an hour costs you. Items with labour add it to their cost."
                            onSave={(labourCost) => saver.saveSettings({ labourCost })}
                        />
                    ) : null}
                </div>
                {can(data, "costs.view") ? <HourMakes data={data} /> : null}
            </Panel>
            <TaxRateNames data={data} disabled={disabled} />
            <Panel title="Tax on purchases">
                <div className="grid gap-4 sm:grid-cols-2">
                    <ChoiceField
                        label={`${taxWord === "tax" ? "Tax" : taxWord} you pay suppliers`}
                        value={s.purchaseTaxClaimed ? "claimed" : "cost"}
                        disabled={disabled}
                        options={[
                            { value: "claimed", label: "Claimed back", hint: "You claim it back, so it isn't part of your costs. Not sure? Ask your accountant." },
                            { value: "cost", label: "A real cost", hint: "You can't claim it back, so it's part of your costs. Not sure? Ask your accountant." },
                        ]}
                        onSave={(v) => saver.saveSettings({ purchaseTaxClaimed: v === "claimed" })}
                    />
                </div>
                {/* Costs never change by themselves (supplierPrices.ts costUpdates). */}
                <p className="m-0 mt-3 text-xs text-muted">
                    Changing this changes what supplier prices really cost. Items whose cost follows one then show &quot;Cost to update&quot; until they&apos;re updated.
                </p>
            </Panel>
            <Panel title="Supplier prices">
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                        label="Check due after"
                        value={String(s.recheckDays)}
                        suffix="days"
                        inputMode="numeric"
                        disabled={disabled}
                        validate={(v) => (Number.isInteger(Number(v)) && Number(v) >= 1 ? null : "Enter a number of days")}
                        hint="Prices not checked for this long show as Check due (amber)."
                        onSave={(v) => saver.saveSettings({ recheckDays: Number(v) })}
                    />
                    <TextField
                        label="Out of date after"
                        value={String(s.outdatedDays)}
                        suffix="days"
                        inputMode="numeric"
                        disabled={disabled}
                        validate={(v) => (Number.isInteger(Number(v)) && Number(v) > s.recheckDays ? null : "Enter more days than Check due")}
                        hint="...and after this long, Out of date (red)."
                        onSave={(v) => saver.saveSettings({ outdatedDays: Number(v) })}
                    />
                </div>
            </Panel>
            <Panel title="Profit">
                <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                        label="Minimum markup"
                        value={String(s.minMarkup)}
                        suffix="%"
                        inputMode="decimal"
                        disabled={disabled}
                        validate={(v) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? null : "Enter a percentage")}
                        hint="Items with a markup on their cost (before tax, its labour included) under this are flagged. 0 flags only items that make nothing."
                        onSave={(v) => saver.saveSettings({ minMarkup: Number(v) })}
                    />
                </div>
            </Panel>
            <Panel title="Name tags" id="name-tags">
                <NameTagSettings data={data} />
            </Panel>
            <JobTimeSettings data={data} />
        </div>
    );
}
